/**
 * Inventory Prediction Agent
 * Predicts product availability based on seasonal patterns, weather, and historical data
 */

interface ProductAvailability {
  product: string;
  currentStock: 'abundant' | 'moderate' | 'limited' | 'out-of-stock';
  predictedAvailability: {
    nextWeek: 'abundant' | 'moderate' | 'limited' | 'out-of-stock';
    twoWeeks: 'abundant' | 'moderate' | 'limited' | 'out-of-stock';
    nextMonth: 'abundant' | 'moderate' | 'limited' | 'out-of-stock';
  };
  confidence: number;
  factors: string[];
  recommendedAction?: string;
}

interface SeasonalTrend {
  product: string;
  peakMonths: number[];
  shoulderMonths: number[];
  offSeasonMonths: number[];
  currentTrend: 'increasing' | 'peak' | 'declining' | 'off-season';
}

interface DemandForecast {
  product: string;
  date: Date;
  expectedDemand: 'high' | 'medium' | 'low';
  factors: string[];
  suggestedOrders: number;
}

class InventoryPredictionAgent {
  private seasonalData: Map<string, SeasonalTrend>;
  private historicalDemand: Map<string, number[]>;

  constructor() {
    this.seasonalData = this.initializeSeasonalData();
    this.historicalDemand = new Map();
  }

  /**
   * Predict product availability
   * @param product Product name
   * @param farmId Farm ID
   * @returns Availability prediction
   */
  predictAvailability(product: string, farmId?: string): ProductAvailability {
    const trend = this.getSeasonalTrend(product);
    const currentMonth = new Date().getMonth();

    // Determine current stock based on seasonal trends
    const currentStock = this.determineCurrentStock(product, currentMonth, trend);

    // Predict future availability
    const nextWeek = this.predictWeekAhead(product, trend, 1);
    const twoWeeks = this.predictWeekAhead(product, trend, 2);
    const nextMonth = this.predictMonthAhead(product, trend);

    // Calculate confidence based on data quality
    const confidence = this.calculateConfidence(product, trend);

    // Identify influencing factors
    const factors = this.identifyFactors(product, trend, currentMonth);

    // Generate recommendation
    const recommendedAction = this.generateRecommendation(currentStock, nextWeek);

    return {
      product,
      currentStock,
      predictedAvailability: {
        nextWeek,
        twoWeeks,
        nextMonth,
      },
      confidence,
      factors,
      recommendedAction,
    };
  }

  /**
   * Get seasonal trend for a product
   * @param product Product name
   * @returns Seasonal trend information
   */
  getSeasonalTrend(product: string): SeasonalTrend {
    return this.seasonalData.get(product.toLowerCase()) || this.getDefaultTrend(product);
  }

  /**
   * Forecast demand for upcoming period
   * @param product Product name
   * @param daysAhead Number of days to forecast
   * @returns Demand forecast
   */
  forecastDemand(product: string, daysAhead: number = 7): DemandForecast[] {
    const forecasts: DemandForecast[] = [];
    const trend = this.getSeasonalTrend(product);

    for (let i = 0; i < daysAhead; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);

      const dayOfWeek = date.getDay();
      const month = date.getMonth();

      // Higher demand on weekends
      const weekendBoost = dayOfWeek === 0 || dayOfWeek === 6 ? 1.5 : 1.0;

      // Seasonal demand multiplier
      const seasonalMultiplier = this.getSeasonalMultiplier(month, trend);

      const baseDemand = 100;
      const adjustedDemand = baseDemand * weekendBoost * seasonalMultiplier;

      const expectedDemand: 'high' | 'medium' | 'low' =
        adjustedDemand > 150 ? 'high' :
        adjustedDemand > 100 ? 'medium' : 'low';

      const factors = this.getDemandFactors(dayOfWeek, month, trend);
      const suggestedOrders = Math.round(adjustedDemand / 10);

      forecasts.push({
        product,
        date,
        expectedDemand,
        factors,
        suggestedOrders,
      });
    }

    return forecasts;
  }

  /**
   * Get products currently in peak season
   * @returns List of peak season products
   */
  getPeakSeasonProducts(): string[] {
    const currentMonth = new Date().getMonth();
    const peakProducts: string[] = [];

    for (const [product, trend] of this.seasonalData.entries()) {
      if (trend.peakMonths.includes(currentMonth)) {
        peakProducts.push(product);
      }
    }

    return peakProducts;
  }

  /**
   * Get products coming into season soon
   * @param weeksAhead Number of weeks to look ahead
   * @returns List of upcoming seasonal products
   */
  getUpcomingSeasonalProducts(weeksAhead: number = 4): string[] {
    const upcoming: string[] = [];
    const currentMonth = new Date().getMonth();
    const futureMonth = (currentMonth + Math.floor(weeksAhead / 4)) % 12;

    for (const [product, trend] of this.seasonalData.entries()) {
      if (
        trend.shoulderMonths.includes(futureMonth) ||
        trend.peakMonths.includes(futureMonth)
      ) {
        if (!trend.peakMonths.includes(currentMonth)) {
          upcoming.push(product);
        }
      }
    }

    return upcoming;
  }

  /**
   * Analyze inventory gaps
   * @param currentInventory Current inventory levels
   * @returns Products that need restocking
   */
  analyzeInventoryGaps(currentInventory: Map<string, number>): Array<{
    product: string;
    currentLevel: number;
    recommendedLevel: number;
    urgency: 'critical' | 'high' | 'medium' | 'low';
  }> {
    const gaps: Array<any> = [];

    for (const [product, level] of currentInventory.entries()) {
      const forecast = this.forecastDemand(product, 7);
      const weekDemand = forecast.reduce((sum, f) => sum + f.suggestedOrders, 0);
      const recommendedLevel = weekDemand * 1.5; // 50% buffer

      if (level < recommendedLevel) {
        const urgency =
          level < recommendedLevel * 0.25 ? 'critical' :
          level < recommendedLevel * 0.5 ? 'high' :
          level < recommendedLevel * 0.75 ? 'medium' : 'low';

        gaps.push({
          product,
          currentLevel: level,
          recommendedLevel: Math.round(recommendedLevel),
          urgency,
        });
      }
    }

    return gaps.sort((a, b) => {
      const urgencyOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
      return urgencyOrder[a.urgency] - urgencyOrder[b.urgency];
    });
  }

  // Helper methods

  private initializeSeasonalData(): Map<string, SeasonalTrend> {
    const data = new Map<string, SeasonalTrend>();

    // Florida seasonal produce data
    data.set('strawberries', {
      product: 'Strawberries',
      peakMonths: [0, 1, 2], // Jan-Mar
      shoulderMonths: [11, 3], // Dec, Apr
      offSeasonMonths: [5, 6, 7, 8, 9],
      currentTrend: this.determineCurrentTrendFromMonths([0, 1, 2]),
    });

    data.set('tomatoes', {
      product: 'Tomatoes',
      peakMonths: [2, 3, 4, 10, 11], // Mar-May, Nov-Dec
      shoulderMonths: [1, 5, 9],
      offSeasonMonths: [6, 7, 8],
      currentTrend: this.determineCurrentTrendFromMonths([2, 3, 4, 10, 11]),
    });

    data.set('blueberries', {
      product: 'Blueberries',
      peakMonths: [3, 4, 5], // Apr-Jun
      shoulderMonths: [2, 6],
      offSeasonMonths: [8, 9, 10, 11, 0, 1],
      currentTrend: this.determineCurrentTrendFromMonths([3, 4, 5]),
    });

    data.set('peppers', {
      product: 'Peppers',
      peakMonths: [3, 4, 5, 6], // Apr-Jul
      shoulderMonths: [2, 7],
      offSeasonMonths: [9, 10, 11, 0, 1],
      currentTrend: this.determineCurrentTrendFromMonths([3, 4, 5, 6]),
    });

    data.set('lettuce', {
      product: 'Lettuce',
      peakMonths: [10, 11, 0, 1, 2], // Nov-Mar
      shoulderMonths: [3, 9],
      offSeasonMonths: [5, 6, 7, 8],
      currentTrend: this.determineCurrentTrendFromMonths([10, 11, 0, 1, 2]),
    });

    return data;
  }

  private determineCurrentTrendFromMonths(peakMonths: number[]): SeasonalTrend['currentTrend'] {
    const currentMonth = new Date().getMonth();

    if (peakMonths.includes(currentMonth)) {
      return 'peak';
    }

    // Check if we're approaching peak
    const nextMonth = (currentMonth + 1) % 12;
    if (peakMonths.includes(nextMonth)) {
      return 'increasing';
    }

    // Check if we're leaving peak
    const prevMonth = (currentMonth - 1 + 12) % 12;
    if (peakMonths.includes(prevMonth)) {
      return 'declining';
    }

    return 'off-season';
  }

  private determineCurrentStock(
    product: string,
    month: number,
    trend: SeasonalTrend
  ): ProductAvailability['currentStock'] {
    if (trend.peakMonths.includes(month)) return 'abundant';
    if (trend.shoulderMonths.includes(month)) return 'moderate';
    if (trend.offSeasonMonths.includes(month)) return 'limited';
    return 'out-of-stock';
  }

  private predictWeekAhead(
    product: string,
    trend: SeasonalTrend,
    weeks: number
  ): ProductAvailability['currentStock'] {
    const futureMonth = new Date();
    futureMonth.setDate(futureMonth.getDate() + weeks * 7);
    const month = futureMonth.getMonth();

    return this.determineCurrentStock(product, month, trend);
  }

  private predictMonthAhead(product: string, trend: SeasonalTrend): ProductAvailability['currentStock'] {
    const nextMonth = (new Date().getMonth() + 1) % 12;
    return this.determineCurrentStock(product, nextMonth, trend);
  }

  private calculateConfidence(product: string, trend: SeasonalTrend): number {
    // Base confidence on how well-established the seasonal pattern is
    const baseConfidence = 70;
    const peakMonthsBonus = trend.peakMonths.length * 5;
    return Math.min(95, baseConfidence + peakMonthsBonus);
  }

  private identifyFactors(product: string, trend: SeasonalTrend, month: number): string[] {
    const factors: string[] = [];

    if (trend.peakMonths.includes(month)) {
      factors.push('Currently in peak season');
    } else if (trend.shoulderMonths.includes(month)) {
      factors.push('Transitional season');
    } else {
      factors.push('Off-season for this product');
    }

    factors.push('Historical Florida growing patterns');
    factors.push('Local farm production schedules');

    return factors;
  }

  private generateRecommendation(
    current: ProductAvailability['currentStock'],
    nextWeek: ProductAvailability['currentStock']
  ): string | undefined {
    if (current === 'abundant' && nextWeek === 'moderate') {
      return 'Stock up now while supplies are plentiful';
    }
    if (current === 'moderate' && nextWeek === 'limited') {
      return 'Order soon - availability decreasing';
    }
    if (current === 'limited' && nextWeek === 'out-of-stock') {
      return 'Last chance to purchase - out of season soon';
    }
    if (current === 'limited' && nextWeek === 'moderate') {
      return 'Availability improving - more supply coming';
    }
    return undefined;
  }

  private getSeasonalMultiplier(month: number, trend: SeasonalTrend): number {
    if (trend.peakMonths.includes(month)) return 1.5;
    if (trend.shoulderMonths.includes(month)) return 1.0;
    if (trend.offSeasonMonths.includes(month)) return 0.3;
    return 1.0;
  }

  private getDemandFactors(dayOfWeek: number, month: number, trend: SeasonalTrend): string[] {
    const factors: string[] = [];

    if (dayOfWeek === 0 || dayOfWeek === 6) {
      factors.push('Weekend - higher traffic expected');
    }

    if (trend.peakMonths.includes(month)) {
      factors.push('Peak season demand');
    }

    return factors;
  }

  private getDefaultTrend(product: string): SeasonalTrend {
    return {
      product,
      peakMonths: [3, 4, 5],
      shoulderMonths: [2, 6],
      offSeasonMonths: [8, 9, 10, 11, 0, 1],
      currentTrend: 'off-season',
    };
  }
}

// Export singleton instance
export const inventoryPredictionAgent = new InventoryPredictionAgent();
export default inventoryPredictionAgent;
